import { useState, useEffect } from "react";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { supabase } from "@/lib/supabase";
import { Button } from "./ui/button";
import { Input } from "./ui/input";
import { useToast } from "@/hooks/use-toast";
import { Loader2, Mail, Settings, TestTube, CheckCircle } from "lucide-react";
import { Card, CardContent, CardHeader, CardTitle } from "./ui/card";
import { Switch } from "./ui/switch";
import EmailForwardingManager from "./EmailForwardingManager";

export const EmailSettingsManager = () => {
  const { toast } = useToast();
  const queryClient = useQueryClient();
  const [testing, setTesting] = useState(false);

  const [settings, setSettings] = useState({
    smtp_host: "",
    smtp_port: 587,
    smtp_user: "",
    smtp_password: "",
    from_email: "",
    from_name: "Shell & Sands Resort",
    forwarding_email: "",
    is_active: true
  });

  // Fetch email settings
  const { data: emailSettings, isLoading } = useQuery({
    queryKey: ["email-settings"],
    queryFn: async () => {
      const { data, error } = await supabase
        .from("email_settings")
        .select("*")
        .eq("is_active", true)
        .single();
      if (error && error.code !== 'PGRST116') throw error;
      return data;
    }
  });

  // Update settings state when data loads
  useEffect(() => {
    if (emailSettings) {
      setSettings({
        smtp_host: emailSettings.smtp_host || "",
        smtp_port: emailSettings.smtp_port || 587,
        smtp_user: emailSettings.smtp_user || "",
        smtp_password: emailSettings.smtp_password || "",
        from_email: emailSettings.from_email || "",
        from_name: emailSettings.from_name || "Shell & Sands Resort",
        forwarding_email: emailSettings.forwarding_email || "",
        is_active: emailSettings.is_active ?? true
      });
    }
  }, [emailSettings]);

  // Save email settings
  const saveSettings = useMutation({
    mutationFn: async () => {
      if (emailSettings) {
        const { error } = await supabase
          .from("email_settings")
          .update(settings)
          .eq("id", emailSettings.id);
        if (error) throw error;
      } else {
        const { error } = await supabase
          .from("email_settings")
          .insert([settings]);
        if (error) throw error;
      }
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["email-settings"] });
      toast({
        title: "Success",
        description: "Email settings saved successfully",
      });
    },
    onError: (error) => {
      toast({
        title: "Error",
        description: "Failed to save email settings",
        variant: "destructive",
      });
    }
  });

  // Test email configuration
  const testEmail = async () => {
    setTesting(true);
    try {
      const { error } = await supabase.functions.invoke('send-quote-email', {
        body: {
          quoteData: { id: 'test-' + Date.now() },
          customerData: {
            name: 'Test Customer',
            email: settings.from_email,
            phone: '+1234567890',
            message: 'This is a test email to verify SMTP configuration.'
          },
          packageDetails: {
            name: 'Test Package',
            totalPrice: 999.99,
            details: {
              persons: 2,
              nights: 3,
              selectedRoom: null,
              selectedAddons: [],
              extraPersons: 0,
              breakdown: {
                basePrice: 999.99,
                personCost: 0,
                nightCost: 0,
                roomCost: 0,
                addonsCost: 0
              }
            }
          }
        }
      });

      if (error) throw error;

      toast({
        title: "Test Email Sent",
        description: "Test email sent successfully! Check your inbox.",
      });
    } catch (error) {
      console.error('Test email error:', error);
      toast({
        title: "Test Failed",
        description: "Failed to send test email. Please check your settings.",
        variant: "destructive",
      });
    } finally {
      setTesting(false);
    }
  };

  if (isLoading) {
    return (
      <Card>
        <CardContent className="flex items-center justify-center p-8">
          <Loader2 className="w-6 h-6 animate-spin" />
        </CardContent>
      </Card>
    );
  }

  return (
    <Card>
      <CardHeader>
        <CardTitle className="flex items-center gap-2">
          <Mail className="w-5 h-5" />
          Email Settings (SMTP Configuration)
        </CardTitle>
      </CardHeader>
      <CardContent className="space-y-6">
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          <div>
            <label className="block text-sm font-medium mb-2">SMTP Host</label>
            <Input
              value={settings.smtp_host}
              onChange={(e) => setSettings(prev => ({ ...prev, smtp_host: e.target.value }))}
              placeholder="smtp.gmail.com"
            />
          </div>

          <div>
            <label className="block text-sm font-medium mb-2">SMTP Port</label>
            <Input
              type="number"
              value={settings.smtp_port}
              onChange={(e) => setSettings(prev => ({ ...prev, smtp_port: parseInt(e.target.value) || 587 }))}
              placeholder="587"
            />
          </div>

          <div>
            <label className="block text-sm font-medium mb-2">SMTP Username</label>
            <Input
              value={settings.smtp_user}
              onChange={(e) => setSettings(prev => ({ ...prev, smtp_user: e.target.value }))}
              placeholder="your-email@gmail.com"
            />
          </div>

          <div>
            <label className="block text-sm font-medium mb-2">SMTP Password</label>
            <Input
              type="password"
              value={settings.smtp_password}
              onChange={(e) => setSettings(prev => ({ ...prev, smtp_password: e.target.value }))}
              placeholder="your-app-password"
            />
          </div>

          <div>
            <label className="block text-sm font-medium mb-2">From Email</label>
            <Input
              type="email"
              value={settings.from_email}
              onChange={(e) => setSettings(prev => ({ ...prev, from_email: e.target.value }))}
              placeholder="noreply@yourresort.com"
            />
          </div>

          <div>
            <label className="block text-sm font-medium mb-2">From Name</label>
            <Input
              value={settings.from_name}
              onChange={(e) => setSettings(prev => ({ ...prev, from_name: e.target.value }))}
              placeholder="Shell & Sands Resort"
            />
          </div>

          <div className="md:col-span-2">
            <label className="block text-sm font-medium mb-2">Forwarding Email (Admin Notifications)</label>
            <Input
              type="email"
              value={settings.forwarding_email}
              onChange={(e) => setSettings(prev => ({ ...prev, forwarding_email: e.target.value }))}
              placeholder="salaam.1170@maroshi.com"
            />
            <p className="text-sm text-gray-500 mt-1">All quote requests and contact forms will be forwarded to this email address</p>
          </div>
        </div>

        <div className="flex items-center space-x-2">
          <Switch
            checked={settings.is_active}
            onCheckedChange={(checked) => setSettings(prev => ({ ...prev, is_active: checked }))}
          />
          <label className="text-sm font-medium">Active Configuration</label>
        </div>

        <div className="flex gap-4">
          <Button 
            onClick={() => saveSettings.mutate()} 
            disabled={saveSettings.isPending}
          >
            {saveSettings.isPending ? (
              <>
                <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                Saving...
              </>
            ) : (
              <>
                <Settings className="w-4 h-4 mr-2" />
                Save Settings
              </>
            )}
          </Button>

          <Button 
            variant="outline" 
            onClick={testEmail} 
            disabled={testing || !settings.smtp_host || !settings.from_email}
          >
            {testing ? (
              <>
                <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                Testing...
              </>
            ) : (
              <>
                <TestTube className="w-4 h-4 mr-2" />
                Test Email
              </>
            )}
          </Button>
        </div>

        <div className="bg-blue-50 p-4 rounded-lg">
          <h4 className="font-medium text-blue-900 mb-2">SMTP Configuration Help</h4>
          <div className="text-sm text-blue-800 space-y-1">
            <p><strong>Gmail:</strong> Use smtp.gmail.com, port 587, and an App Password</p>
            <p><strong>Outlook:</strong> Use smtp-mail.outlook.com, port 587</p>
            <p><strong>Custom SMTP:</strong> Contact your hosting provider for settings</p>
          </div>
        </div>

        {emailSettings && (
          <div className="bg-green-50 p-4 rounded-lg">
            <div className="flex items-center gap-2 text-green-800">
              <CheckCircle className="w-4 h-4" />
              <span className="font-medium">Email configuration is active</span>
            </div>
          </div>
        )}
      </CardContent>
    </Card>
  );
};